import { companies } from "@/api/addressBook.service";
import { MOVE_TABLE_KEYS, type MoveTableKey } from "@/api/profiles/companyContentMoveService";

/**
 * Human labels for the 15 movable profiles.* tables. Deliberately a local
 * copy of MoveCompanyContentModal's map (kept private there) rather than a
 * refactor of the shipped modal — the key set is pinned by MOVE_TABLE_KEYS,
 * so the two cannot drift structurally.
 */
export const TABLE_LABELS: Record<MoveTableKey, string> = {
  lawyer_profiles: "Lawyer profiles",
  firm_profiles: "Firm profiles",
  firm_tab_general: "Firm tab: General",
  firm_tab_awards: "Firm tab: Awards",
  firm_tab_client_testimonials: "Firm tab: Client testimonials",
  firm_tab_diversity: "Firm tab: Diversity",
  firm_tab_international_capabilities: "Firm tab: International capabilities",
  firm_tab_multimedia: "Firm tab: Multimedia",
  firm_tab_notable_cases: "Firm tab: Notable cases",
  firm_tab_partner_perspectives: "Firm tab: Partner perspectives",
  firm_tab_sponsorship: "Firm tab: Sponsorship",
  firm_tab_webinar: "Firm tab: Webinar",
  firm_tab_video: "Firm tab: Video",
  team_pages: "Team pages",
  firm_articles: "Articles",
};

/**
 * Resolves a company's display name, tolerating the two states a MERGED-AWAY
 * company id is expected to be in: address-book 404s (unknown id), or 200s
 * dereferenced to the merge SURVIVOR — whose id differs from the requested
 * one and whose name must not be presented as the requested company's. Both
 * resolve to null (caller falls back to the raw uuid); a lookup failure must
 * never block this page, whose rows exist precisely because such dead
 * companies needed cleaning up.
 */
export async function resolveCompanyName(id: string): Promise<string | null> {
  try {
    const response = await companies.getById(id);
    return response.data.id === id ? response.data.name : null;
  } catch {
    return null;
  }
}

/** Non-zero (table, count) pairs, in the API's table order. */
export function nonZeroCounts(counts: Record<MoveTableKey, number>): Array<[MoveTableKey, number]> {
  return MOVE_TABLE_KEYS.filter(key => counts[key] > 0).map(key => [key, counts[key]]);
}

/** Non-zero per-table counts derived from a movedIds manifest, in table order. */
export function manifestCounts(
  movedIds: Record<MoveTableKey, string[]>,
): Array<[MoveTableKey, number]> {
  return MOVE_TABLE_KEYS.filter(key => (movedIds[key]?.length ?? 0) > 0).map(key => [
    key,
    movedIds[key].length,
  ]);
}

/** The JSON shape the C6 move modal downloads — kept identical so manifests are interchangeable. */
export type MoveManifest = {
  sourceCompanyId: string;
  targetCompanyId: string;
  movedIds: Record<MoveTableKey, string[]>;
  totalMoved: number;
  appliedAt: string;
};

export function downloadManifest(manifest: MoveManifest): void {
  const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `company-content-move-manifest-${new Date()
    .toISOString()
    .replace(/[:.]/g, "-")}.json`;
  anchor.click();
  // Deferred: revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
